import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { fetchOrderDetail, removeWasserijItemExtra, setOrderConfirmations, updateOrderFields, OrderConflictError, type OrderDetail, type OrderFailedAttempt, type OrderItem, type OrderItemGroup } from '../../lib/orders';
import { syncOrderFromMollie } from '../../lib/sync-mollie';
import { salesforceClient } from '../../lib/salesforce/client';
import { SendEtaAction } from './SendEtaAction';
import { supabase } from '../../lib/supabase';
import type { RepeatOrderPrefill } from '../../lib/repeat-order-prefill';
import { fetchOfferteDetail } from '../../lib/offertes';
import { RepeatOrderDialog } from './RepeatOrderDialog';
import { BRAND_COLORS, ITEM_STATUS_COLORS, ORDER_STATUS_COLORS } from '../../lib/order-constants';
import { formatCurrency, formatDate, formatDateTime } from '../../lib/format';
import { useAppointmentSchedulerStore } from '../../stores/appointment-scheduler-store';
import { useWasserijItemDrawerStore } from '../../stores/wasserij-item-drawer-store';
import { canPerform, canRegisterKlacht, canEditNotes, isPackageSku } from '../../lib/order-edit-rules';
import { useUserRole } from '../../hooks/useUserRole';
import { KlachtBadge } from '../klachten/KlachtBadge';
import { CallButton } from '../phone/CallButton';
import { PhoneNumber } from '../phone/PhoneNumber';
import type { Brand } from '../../lib/telephony';
import { VervolgWoBadge } from '../klachten/VervolgWoBadge';
import { KlachtSummaryCard } from '../klachten/KlachtSummaryCard';
import { klachtStatusLabel } from '../../lib/klacht-constants';
import { BerichtThread } from '../berichten/BerichtThread';
import { KlachtRegisterDialog } from '../klachten/KlachtRegisterDialog';
import { useKlachtDialogStore } from '../../stores/klacht-dialog-store';
import { EditableOrderItems } from './EditableOrderItems';
import { OpslagStandBadge } from './OpslagStandBadge';
import { fetchKleedTijdlijn, type OpslagStand } from '../../lib/kleed-tijdlijn';
import { EditWasserijItemDialog } from './EditWasserijItemDialog';
import { AddKleedDialog } from './AddKleedDialog';
import { CancelOrderDialog } from './CancelOrderDialog';
import { CancelAppointmentDialog } from './CancelAppointmentDialog';
import { MarkPickedUpDialog } from './MarkPickedUpDialog';
import { NaleveringDialog } from './NaleveringDialog';
import { DeleteOrderDialog } from './DeleteOrderDialog';
import { SendEmailDialog } from './SendEmailDialog';
import { RefundDialog } from './RefundDialog';
import { MaakWebshopGratisDialog } from '../webshop/MaakWebshopGratisDialog';
import { OpFactuurToggleDialog } from './OpFactuurToggleDialog';
import { B2BRetourlabelButton } from '../klanten/B2BRetourlabelButton';
import { isB2BCyclusAccount } from '../../lib/b2b-accounts';
import { CreateInvoiceDialog } from './CreateInvoiceDialog';
import { ReleaseWerkOrderDialog } from './ReleaseWerkOrderDialog';
import { RefundsSection } from './RefundsSection';
import { OphaalReminderCard } from './OphaalReminderCard';
import { SnoozeRemindersDialog } from './SnoozeRemindersDialog';
import { RestoreOrderCard } from './RestoreOrderCard';
import { SelfServiceCompleteCard } from './SelfServiceCompleteCard';
import { AddressOverrideDialog } from './AddressOverrideDialog';
import { EditOrderContactDialog } from './EditOrderContactDialog';
import { EditInvoiceNameDialog } from './EditInvoiceNameDialog';
import { DatumWindowDialog } from './DatumWindowDialog';
import { WasserijItemBehandelingDialog } from './WasserijItemBehandelingDialog';
import { AddFeeDialog } from './AddFeeDialog';
import { ActivityFeed } from './ActivityFeed';
import { EditableNotes } from './EditableNotes';
import { OrderFlagsRow } from './OrderFlagsRow';
import { StatusEditPopover } from './StatusEditPopover';
import { KortingPill } from './KortingPill';
import { MarketingContextChip } from './MarketingContextChip';
import { BundelkortingToggle } from '../common/BundelkortingToggle';
import { UpsellSection } from './UpsellSection';
import { ReparatieSection } from './ReparatieSection';
import { PlanningNotesSection } from './PlanningNotesSection';
import { TransactiesSection } from '../transacties/TransactiesSection';
import { MediaGrid } from '../MediaGrid';

type Variant = 'page' | 'drawer';

interface OrderDetailViewProps {
  orderId: string;
  variant: Variant;
  onClose?: () => void;
}

function isWebshopOrder(order: OrderDetail): boolean {
  return order.recordType === 'Webshop';
}

function isSofaSaverOrder(order: OrderDetail): boolean {
  return order.recordType === 'SofaSaver';
}

function isWasserijOrder(order: OrderDetail): boolean {
  return order.recordType === 'Wasserij';
}

function renderAddress(formatted: string) {
  if (!formatted) return null;
  const lines = formatted.split(/<br\s*\/?>/i).map(s => s.trim()).filter(Boolean);
  return lines.map((line, i) => (
    <span key={i} className="block">{line}</span>
  ));
}

function ItemRow({ item, webshop, isExtra, clickable }: { item: OrderItem; webshop: boolean; isExtra?: boolean; clickable: boolean }) {
  const openDrawer = useWasserijItemDrawerStore((s) => s.open);
  const dimensions = item.isRound && item.diameterCm
    ? `Ø ${item.diameterCm} cm`
    : item.lengthCm && item.widthCm
      ? `${item.lengthCm}×${item.widthCm} cm`
      : '';

  const content = (
    <>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          {isExtra && (
            <span className="text-slate-300 text-xs">↳</span>
          )}
          <span className={clsx(
            'truncate',
            isExtra ? 'text-sm text-slate-700' : 'text-sm font-medium text-slate-900'
          )}>
            {item.name || 'Item'}
          </span>
          {!isExtra && item.itemNumber && (
            <span className="text-[11px] text-slate-400 font-mono">{item.itemNumber}</span>
          )}
          {!isExtra && item.photosUrl && (
            <PhotosLink url={item.photosUrl} />
          )}
          {item.status && !isExtra && (
            <span
              className={clsx(
                'text-[10px] px-1.5 py-0.5 rounded font-medium',
                ITEM_STATUS_COLORS[item.status] || 'bg-slate-100 text-slate-600'
              )}
            >
              {item.status}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          {webshop ? (
            <span>Aantal: {item.quantity}</span>
          ) : (
            <>
              {!isExtra && dimensions && <span>{dimensions}</span>}
              {!isExtra && item.areaM2 != null && <span>{item.areaM2.toFixed(2)} m²</span>}
              {/* Aantal__c valt bij kleden samen met Aantal_m__c. Verberg `×N`
                  dubbel-render tenzij het echt een stukstal is. */}
              {item.quantity > 1 &&
                (item.areaM2 == null || Math.abs(item.quantity - item.areaM2) > 0.01) && (
                  <span>×{item.quantity}</span>
                )}
            </>
          )}
        </div>
      </div>
      <div className={clsx(
        'flex-shrink-0',
        isExtra ? 'text-sm text-slate-600' : 'text-sm font-medium text-slate-900'
      )}>
        {formatCurrency(item.priceInclBtw)}
      </div>
    </>
  );

  if (clickable) {
    return (
      <button
        type="button"
        onClick={() => openDrawer(item.id)}
        className={clsx(
          'w-full text-left px-6 py-3 flex items-center gap-4 hover:bg-slate-50 transition-colors',
          isExtra && 'pl-12 bg-slate-50/60 hover:bg-slate-100'
        )}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={clsx(
      'px-6 py-3 flex items-center gap-4',
      isExtra && 'pl-12 bg-slate-50/60'
    )}>
      {content}
    </div>
  );
}

function ItemGroup({ group, webshop, clickable }: { group: OrderItemGroup; webshop: boolean; clickable: boolean }) {
  const subtotal =
    group.primary.priceInclBtw + group.extras.reduce((s, e) => s + e.priceInclBtw, 0);
  return (
    <div className="py-1">
      <ItemRow item={group.primary} webshop={webshop} clickable={clickable} />
      {group.extras.map(extra => (
        <ItemRow key={extra.id} item={extra} webshop={webshop} isExtra clickable={clickable} />
      ))}
      {group.extras.length > 0 && !webshop && (
        <div className="pl-12 pr-6 py-1.5 flex items-center justify-between text-xs text-slate-500 border-t border-dashed border-slate-200">
          <span>Subtotaal</span>
          <span className="font-medium text-slate-700">{formatCurrency(subtotal)}</span>
        </div>
      )}
    </div>
  );
}

// WO-statussen waarin de order nog een ophaal nodig heeft. Combinatie met een
// reeds 'Verwerkt' ophaalafspraak = de na-ophaal-situatie (1 kleed bleef staan,
// of een eerdere ophaal mislukte) → office mag een EXTRA ophaalrit inplannen.
const PICKUP_PLANNING_STATUSES = [
  'Ophalen plannen',
  'Ophaalvoorstel gedaan',
  'Ophaalvoorstel geen reactie',
];
// Spiegel voor afleveren: rit al uitgevoerd maar de WO staat weer in de
// aflever-planfase (nalevering: een kleed bleef bij ons). De office-writer
// annuleert alleen 'Ingepland'-ritten, dus de verwerkte rit blijft historie.
const DELIVERY_PLANNING_STATUSES = ['Terugbrengen plannen', 'Aflevervoorstel gedaan'];

// WO-statussen waarin de ophaal al volledig is afgerond (alle bekende kleden
// binnen), maar er alsnog een vergeten/extra kleed opgehaald moet worden. De
// knop "+ Extra ophaalrit" heropent dan de ophaal: status terug naar
// 'Ophalen plannen' + 'Ophalen handmatig' aan. Bewust afgebakend tot 'Opgehaald'
// (laagste flow-risico — kleden zijn binnen maar nog niet in reiniging).
const EXTRA_PICKUP_REOPEN_STATUSES = ['Opgehaald'];

// WO-statussen waarin "al opgehaald" markeren mag (nog vóór de feitelijke ophaal).
// Spiegel van OfficeMarkPickedUpService.ALLOWED_SOURCE_STATUSES (Apex).
// Achteraf-correctie van een "hele stop klaar"-melding, per richting.
// Spiegel van NALEVERING_RICHTINGEN (api/orders/_nalevering.ts): de WO-statussen
// waarin de stop als afgerond geldt, en de kleedstatussen die "is mee geweest"
// betekenen. Ophalen: de route-afronding stempelt 'Opgehaald' door naar
// 'Klaar voor Voorinspectie', ook voor een kleed dat nooit in de bus lag.
const NALEVERING_STATUSES = ['Teruggebracht', 'Afgerond'];
const NALEVERING_ITEM_STATUSES = ['Afgeleverd'];
const NA_OPHAAL_STATUSES = ['Opgehaald', 'Aan het reinigen'];
const NA_OPHAAL_ITEM_STATUSES = ['Opgehaald', 'Klaar voor Voorinspectie'];

const MARK_PICKED_UP_STATUSES = [
  'Wachten op adresgegevens',
  'Ophalen plannen',
  'Ophaalvoorstel gedaan',
  'Ophaalvoorstel geen reactie',
  'Ophalen ingepland',
];

function AppointmentBlock({
  appointment,
  canPlan,
  canForceCancel,
  onPlan,
  onCancelAppointment,
  allowExtraPickup = false,
  extraPickupBusy = false,
  extraRitLabel = '+ Extra ophaalrit inplannen',
  extraRitTitle = 'Plan een extra ophaalrit voor een kleed dat nog opgehaald moet worden (de uitgevoerde rit blijft staan)',
}: {
  appointment: OrderDetail['pickupAppointment'];
  canPlan: boolean;
  canForceCancel: boolean;
  onPlan?: () => void;
  onCancelAppointment?: (recoveryMode: boolean) => void;
  /** Rit is uitgevoerd maar er moet nóg een rit komen: toon "+ Extra …rit" i.p.v. "Wijzigen". */
  allowExtraPickup?: boolean;
  extraPickupBusy?: boolean;
  extraRitLabel?: string;
  extraRitTitle?: string;
}) {
  if (!appointment) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-slate-400">Nog geen afspraak ingepland</p>
        {canPlan && onPlan && (
          <button
            type="button"
            onClick={onPlan}
            className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            Plan afspraak
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      {appointment.label && (
        <p className="text-sm text-slate-900">{appointment.label}</p>
      )}
      {appointment.vanaf && (
        <p className="text-xs text-slate-500">
          {formatDateTime(appointment.vanaf)}
          {appointment.tot && ` – ${formatDateTime(appointment.tot)}`}
        </p>
      )}
      {appointment.wachtOpRouteLock && (
        <p className="text-xs text-slate-500">
          Tijdvenster volgt zodra de route gelockt is
        </p>
      )}
      <div className="flex items-center gap-2 pt-0.5 flex-wrap">
        {appointment.isDefinitief && (
          <span className="inline-block text-[10px] px-1.5 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700">
            Ingepland
          </span>
        )}
        {appointment.buitenDienstgebied && (
          <span className="inline-block text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-800">
            Buiten dienstgebied
          </span>
        )}
        {canPlan && onPlan && !allowExtraPickup && (
          <button
            type="button"
            onClick={onPlan}
            className="text-xs text-indigo-600 hover:underline"
          >
            Wijzigen
          </button>
        )}
        {canPlan && onCancelAppointment && appointment.ritId && appointment.routeStopStatus === 'Ingepland' && (
          <button
            type="button"
            onClick={() => onCancelAppointment(false)}
            className="text-xs text-red-600 hover:underline"
          >
            Annuleren
          </button>
        )}
        {canPlan && onCancelAppointment && appointment.ritId && canForceCancel && appointment.routeStopStatus === 'Verwerkt' && (
          <button
            type="button"
            onClick={() => onCancelAppointment(true)}
            className="text-xs text-red-600 hover:underline"
            title="Recovery — annuleer een al uitgevoerde rit (kantoor)"
          >
            Forceer annuleren
          </button>
        )}
      </div>
      {appointment.routeId && (
        <a
          href={`/route-planner?routeId=${appointment.routeId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
        >
          Open route
          <ExternalLinkIcon />
        </a>
      )}
      {appointment.routeStopStatus === 'Verwerkt' && appointment.medewerker && (
        <p className="text-xs text-slate-500">
          Uitgevoerd door{' '}
          <span className="text-slate-700">{appointment.medewerker.name}</span>
        </p>
      )}
      {allowExtraPickup && onPlan && (
        <div className="pt-1">
          <button
            type="button"
            onClick={onPlan}
            disabled={extraPickupBusy}
            className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
            title={extraRitTitle}
          >
            {extraPickupBusy ? 'Bezig…' : extraRitLabel}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Melding onder de (lege) afspraak-sectie wanneer de vorige rit is overgeslagen
 * (klant niet thuis e.d.). De WO is door de skip al teruggezet naar "...plannen",
 * dus de afspraak-sectie is leeg; deze regel houdt de mislukte poging zichtbaar.
 *
 * Met "Toch gelukt?" (admin/medewerker) is de skip terug te draaien via het
 * bestaande route-primitief toch-geslaagd: WO naar Opgehaald/Teruggebracht,
 * kleden schuiven door, no-show-regel weg, klant krijgt de normale bevestiging.
 */
function FailedAttemptNote({
  attempt,
  fase,
  canUndo,
  onUndone,
}: {
  attempt: OrderFailedAttempt | null | undefined;
  fase: 'pickup' | 'delivery';
  canUndo: boolean;
  onUndone: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [undoError, setUndoError] = useState<string | null>(null);

  if (!attempt) return null;
  const reden = attempt.reden || 'Niet gelukt';
  const datum = attempt.datum ? formatDate(attempt.datum) : '';
  const undoable = canUndo && !!attempt.routeId && !!attempt.ritId;

  const handleUndo = async () => {
    if (!attempt.routeId || !attempt.ritId) return;
    setBusy(true);
    setUndoError(null);
    try {
      await salesforceClient.markStopsTochGeslaagd(attempt.routeId, [attempt.ritId], {
        notitie: 'teruggedraaid vanaf orderpagina',
      });
      setConfirming(false);
      onUndone();
    } catch (err) {
      setUndoError(err instanceof Error ? err.message : 'Terugdraaien mislukt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-1.5">
      <p className="flex items-start gap-1 text-xs text-amber-700">
        <svg
          className="w-3.5 h-3.5 mt-0.5 shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 9v3.75m0 3.75h.008M11.25 4.5l-7.5 13a.75.75 0 00.65 1.125h15.2a.75.75 0 00.65-1.125l-7.5-13a.75.75 0 00-1.3 0z"
          />
        </svg>
        <span>
          Vorige poging niet gelukt: {reden}
          {datum && <> · {datum}</>}
          {undoable && !confirming && (
            <>
              {' '}
              <button
                type="button"
                onClick={() => setConfirming(true)}
                className="text-indigo-600 hover:text-indigo-700 hover:underline"
                title="De rit is toch gelukt — markeer als geslaagd"
              >
                Toch gelukt?
              </button>
            </>
          )}
        </span>
      </p>
      {undoable && confirming && (
        <div className="mt-1 ml-4 pl-1 text-xs text-slate-600">
          {fase === 'pickup'
            ? 'De order gaat naar Opgehaald, de kleden schuiven door en de klant krijgt de normale ophaalbevestiging.'
            : 'De order gaat naar Teruggebracht en de klant krijgt de normale bezorgbevestiging.'}{' '}
          <button
            type="button"
            onClick={handleUndo}
            disabled={busy}
            className="font-medium text-emerald-700 hover:underline disabled:opacity-50"
          >
            {busy ? 'Bezig…' : 'Markeer als geslaagd'}
          </button>
          {' · '}
          <button
            type="button"
            onClick={() => { setConfirming(false); setUndoError(null); }}
            disabled={busy}
            className="text-slate-500 hover:underline disabled:opacity-50"
          >
            annuleer
          </button>
          {undoError && <p className="mt-0.5 text-red-600">{undoError}</p>}
        </div>
      )}
    </div>
  );
}

function ExternalLinkIcon() {
  return (
    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
    </svg>
  );
}

export function PhotosLink({ url }: { url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title="Foto's openen"
      className="inline-flex items-center gap-1 text-[11px] text-indigo-600 hover:text-indigo-700 hover:underline"
    >
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
      </svg>
      Foto's
    </a>
  );
}

export function OrderDetailView({ orderId, variant, onClose }: OrderDetailViewProps) {
  const [opslag, setOpslag] = useState<OpslagStand | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchKleedTijdlijn({ woId: orderId })
      .then((r) => { if (!cancelled) setOpslag(r.opslag); })
      .catch(() => { if (!cancelled) setOpslag(null); });
    return () => { cancelled = true; };
  }, [orderId]);
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSendEmailDialog, setShowSendEmailDialog] = useState(false);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [showMaakGratisDialog, setShowMaakGratisDialog] = useState(false);
  const [showOpFactuurDialog, setShowOpFactuurDialog] = useState(false);
  const [showCreateInvoiceDialog, setShowCreateInvoiceDialog] = useState(false);
  const [showReleaseInvoiceDialog, setShowReleaseInvoiceDialog] = useState(false);
  const [refundsRefreshKey, setRefundsRefreshKey] = useState(0);
  const [repeatPrefill, setRepeatPrefill] = useState<RepeatOrderPrefill | null>(null);
  // Lead-id van een net aangemaakte vervolgorder (channel ORDER) — de conversie
  // naar Werk Order loopt async in SF (LeadConversionQueueable), dus we pollen
  // tot de order bestaat en navigeren dan pas door.
  const [pendingVervolgLeadId, setPendingVervolgLeadId] = useState<string | null>(null);
  const [repeatLoading, setRepeatLoading] = useState(false);
  const [repeatError, setRepeatError] = useState<string | null>(null);
  const [addressDialogSide, setAddressDialogSide] = useState<'pickup' | 'delivery' | null>(null);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showInvoiceNameDialog, setShowInvoiceNameDialog] = useState(false);
  const [datumDialogSide, setDatumDialogSide] = useState<'pickup' | 'delivery' | null>(null);
  const [cancelAppointmentTarget, setCancelAppointmentTarget] = useState<
    { ritId: string; fase: 'pickup' | 'delivery'; datum: string | null; recoveryMode: boolean } | null
  >(null);
  const [showMarkPickedUp, setShowMarkPickedUp] = useState(false);
  const [showNalevering, setShowNalevering] = useState<'afleveren' | 'ophalen' | null>(null);
  const [showSnoozeDialog, setShowSnoozeDialog] = useState(false);
  const { role } = useUserRole();
  // Forceer-annuleren (recovery op een al uitgevoerde rit) mag kantoor doen —
  // admin en medewerker. Callcenter blijft buiten dit destructieve pad.
  const canForceCancel = role === 'admin' || role === 'medewerker';
  const [behandelingTarget, setBehandelingTarget] = useState<{ itemId: string; name: string } | null>(null);
  const [showFeeDialog, setShowFeeDialog] = useState(false);
  const [showAddKleedDialog, setShowAddKleedDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<OrderItem | null>(null);
  const [removingExtraId, setRemovingExtraId] = useState<string | null>(null);
  const [extraRemoveError, setExtraRemoveError] = useState<string | null>(null);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const [enablingConfirmations, setEnablingConfirmations] = useState(false);
  const [confirmationsError, setConfirmationsError] = useState<string | null>(null);
  const [planningExtraPickup, setPlanningExtraPickup] = useState(false);
  const [extraPickupError, setExtraPickupError] = useState<string | null>(null);
  const [syncingMollie, setSyncingMollie] = useState(false);
  const [syncMollieMsg, setSyncMollieMsg] = useState<{ kind: 'ok' | 'info' | 'error'; text: string } | null>(null);
  const openForPickup = useAppointmentSchedulerStore((s) => s.openForPickup);
  const openForDelivery = useAppointmentSchedulerStore((s) => s.openForDelivery);
  const openKlachtDialog = useKlachtDialogStore((s) => s.open);

  const refetchOrder = useCallback(() => {
    fetchOrderDetail(orderId)
      .then(setOrder)
      .catch(() => {/* stil */});
  }, [orderId]);

  // Haal de betaalstatus direct bij Mollie op en boek de betaling als die er is.
  // Vangnet voor een gemiste Mollie-webhook — sneller dan de 4-uurs sync-batch.
  const handleSyncMollie = useCallback(async () => {
    setSyncingMollie(true);
    setSyncMollieMsg(null);
    try {
      const r = await syncOrderFromMollie(orderId);
      if (!r.ok) {
        setSyncMollieMsg({ kind: 'error', text: r.error || 'Mollie-sync mislukt' });
      } else if (r.paymentFound) {
        setSyncMollieMsg(
          r.volledigBetaald
            ? { kind: 'ok', text: 'Betaling gevonden — order staat nu op betaald.' }
            : { kind: 'ok', text: `Betaling geboekt — nog ${formatCurrency(r.openstaandBedrag ?? 0)} open.` }
        );
        refetchOrder();
        setActivityRefreshKey((k) => k + 1);
      } else {
        setSyncMollieMsg({ kind: 'info', text: 'Nog geen betaling bij Mollie gevonden.' });
      }
    } catch (err) {
      setSyncMollieMsg({ kind: 'error', text: err instanceof Error ? err.message : 'Mollie-sync mislukt' });
    } finally {
      setSyncingMollie(false);
    }
  }, [orderId, refetchOrder]);

  // Vervolgorder met "Maak opdracht": poll tot de async lead-conversie de
  // Werk Order heeft opgeleverd (offertes/detail → 409 ALREADY_CONVERTED met
  // workOrderId), navigeer dan direct naar de nieuwe order. Fallback na 30s:
  // naar de offerte-pagina (die redirect zelf zodra de order er alsnog is).
  useEffect(() => {
    if (!pendingVervolgLeadId) return;
    let cancelled = false;
    const startedAt = Date.now();
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      if (cancelled) return;
      try {
        await fetchOfferteDetail(pendingVervolgLeadId);
        // Laadt nog als offerte → conversie loopt nog, opnieuw proberen.
      } catch (err) {
        const e = err as Error & { code?: string; workOrderId?: string };
        if (e.code === 'ALREADY_CONVERTED' && e.workOrderId) {
          if (!cancelled) {
            setPendingVervolgLeadId(null);
            navigate(`/orders/${e.workOrderId}`);
          }
          return;
        }
        // Andere fouten (bv. NO_SIGNATURE vóór conversie): gewoon doorpollen.
      }
      if (cancelled) return;
      if (Date.now() - startedAt > 30000) {
        setPendingVervolgLeadId(null);
        navigate(`/offertes/${pendingVervolgLeadId}`);
        return;
      }
      timer = setTimeout(poll, 2000);
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [pendingVervolgLeadId, navigate]);

  const handleOpenRepeatDialog = useCallback(async () => {
    setRepeatLoading(true);
    setRepeatError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers: Record<string, string> = {};
      if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
      const res = await fetch(`/api/orders/repeat-prefill?id=${encodeURIComponent(orderId)}`, { headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setRepeatError(data.errorMessage || data.errorCode || 'Kon vervolgorder niet voorbereiden');
        return;
      }
      setRepeatPrefill(data.prefill as RepeatOrderPrefill);
    } catch (err) {
      setRepeatError(err instanceof Error ? err.message : 'Onverwachte fout');
    } finally {
      setRepeatLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);
    fetchOrderDetail(orderId)
      .then((data) => {
        if (cancelled) return;
        setOrder(data);
        setLoading(false);
      })
      .catch((err: Error & { status?: number }) => {
        if (cancelled) return;
        if (err.status === 404) {
          setNotFound(true);
        } else {
          setError(err.message || 'Kon order niet laden');
        }
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [orderId]);

  // Refetch de order wanneer de wasserij-item drawer een mutatie heeft
  // uitgevoerd — nieuwe status/voorinspectie/etc. moet zichtbaar zijn in de lijst.
  useEffect(() => {
    const handler = () => {
      refetchOrder();
      setActivityRefreshKey((k) => k + 1);
    };
    window.addEventListener('wasserij-item:updated', handler);
    return () => window.removeEventListener('wasserij-item:updated', handler);
  }, [refetchOrder]);

  // Status/flag-edits dispatchen `order:conflict` bij 409 — refetch om client
  // weer in sync te brengen met SF.
  useEffect(() => {
    const handler = () => refetchOrder();
    window.addEventListener('order:conflict', handler);
    return () => window.removeEventListener('order:conflict', handler);
  }, [refetchOrder]);

  const isDrawer = variant === 'drawer';

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
          <p className="text-sm text-slate-500">Order laden...</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-3">
        <p className="text-sm">Order niet gevonden</p>
        <button
          onClick={() => (isDrawer && onClose ? onClose() : navigate(-1))}
          className="text-indigo-600 text-sm hover:underline"
        >
          {isDrawer ? 'Sluiten' : 'Terug'}
        </button>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3">
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
          {error || 'Onbekende fout'}
        </div>
        <button
          onClick={() => (isDrawer && onClose ? onClose() : navigate(-1))}
          className="text-indigo-600 text-sm hover:underline"
        >
          {isDrawer ? 'Sluiten' : 'Terug'}
        </button>
      </div>
    );
  }

  const webshop = isWebshopOrder(order);
  const sofasaver = isSofaSaverOrder(order);
  const wasserij = isWasserijOrder(order);
  const totalsExcl = order.totals.totalInclBtw / 1.21;
  const canSchedule = wasserij;
  const canEdit = canPerform('edit_items', order);
  // Ophaal-/aflever-opmerkingen staan los van de item-edit-gate: universele
  // WO-velden, bewerkbaar op alle order-types (ook SofaSaver/SR/Webshop).
  const canEditNotesForOrder = canEditNotes(order);
  const canCancel = canPerform('cancel', order);
  // Na-ophaal: ophaal is al uitgevoerd, maar de WO heeft nog een ophaal nodig
  // (1 kleed bleef staan, of een eerdere ophaal mislukte). Toon dan een knop om
  // een EXTRA ophaalrit te plannen i.p.v. de uitgevoerde rit te willen wijzigen.
  const pickupRitVerwerkt =
    canSchedule && order.pickupAppointment?.routeStopStatus === 'Verwerkt';
  // De WO is al 'Opgehaald' maar er moet alsnog een vergeten kleed worden
  // opgehaald → de knop moet de ophaal eerst heropenen (status terugzetten).
  const reopenForExtraPickup =
    pickupRitVerwerkt && EXTRA_PICKUP_REOPEN_STATUSES.includes(order.status);
  const allowExtraPickup =
    (pickupRitVerwerkt && PICKUP_PLANNING_STATUSES.includes(order.status)) ||
    reopenForExtraPickup;
  // Nalevering: aflevering uitgevoerd, WO weer in de aflever-planfase.
  const allowExtraDelivery =
    canSchedule &&
    order.deliveryAppointment?.routeStopStatus === 'Verwerkt' &&
    DELIVERY_PLANNING_STATUSES.includes(order.status);
  // "Al opgehaald" (geen rit): kleden zijn al binnen (bv. meegenomen bij een
  // aflevering). Alleen vóór de feitelijke ophaal, en niet als er al een
  // uitgevoerde ophaalrit staat (dan is de order al 'Opgehaald').
  const canMarkPickedUp =
    canSchedule &&
    order.recordType === 'Wasserij' &&
    MARK_PICKED_UP_STATUSES.includes(order.status) &&
    order.pickupAppointment?.routeStopStatus !== 'Verwerkt';
  // Nalevering: order is afgeleverd gemeld, maar een kleed bleef bij ons.
  const naleveringItems = order.items
    .map((g) => g.primary)
    .filter((i) => NALEVERING_ITEM_STATUSES.includes(i.status ?? ''));
  const canNalevering =
    canSchedule &&
    order.recordType === 'Wasserij' &&
    NALEVERING_STATUSES.includes(order.status) &&
    naleveringItems.length > 0;
  // Na-ophaal: order is opgehaald gemeld, maar een kleed bleef bij de klant.
  const naOphaalItems = order.items
    .map((g) => g.primary)
    .filter((i) => NA_OPHAAL_ITEM_STATUSES.includes(i.status ?? ''));
  const canNaOphaal =
    canSchedule &&
    order.recordType === 'Wasserij' &&
    NA_OPHAAL_STATUSES.includes(order.status) &&
    naOphaalItems.length > 0;
  const canDelete = canPerform('delete', order);

  const handleDeleted = () => {
    setShowDeleteDialog(false);
    if (variant === 'drawer' && onClose) {
      onClose();
      // Lijstpagina luistert op order:deleted en filtert het record uit zijn cache
      window.dispatchEvent(new CustomEvent('order:deleted', { detail: { orderId: order.id } }));
    } else {
      navigate('/orders');
    }
  };
  const hasOpenKlacht =
    !!order.klachtParentId &&
    order.klachtStatus !== 'Gesloten' &&
    order.klachtStatus !== 'Opgelost';
  // Klachten die óp deze order staan (klachtParentId zegt alleen dat de WO
  // zélf een herbehandeling is). Open eerst; gesloten blijven zichtbaar als
  // historie, zodat een tweede melding bewust bij de eerste landt.
  const klachtenOpOrder = order.klachtenOpOrder ?? [];
  const openKlachtenOpOrder = klachtenOpOrder.filter(
    (k) => k.status !== 'Gesloten' && k.status !== 'Opgelost'
  );
  const geslotenKlachtenOpOrder = klachtenOpOrder.filter(
    (k) => k.status === 'Gesloten' || k.status === 'Opgelost'
  );
  const canRegisterNewKlacht =
    canRegisterKlacht(order) && !hasOpenKlacht && openKlachtenOpOrder.length === 0;

  const handleRemovePaidExtra = async (childItemId: string) => {
    if (removingExtraId) return;
    setRemovingExtraId(childItemId);
    setExtraRemoveError(null);
    try {
      await removeWasserijItemExtra(childItemId);
      refetchOrder();
      setActivityRefreshKey((k) => k + 1);
    } catch (err) {
      setExtraRemoveError(err instanceof Error ? err.message : 'Verwijderen mislukt');
    } finally {
      setRemovingExtraId(null);
    }
  };

  const handleSaved = (nieuweLastModifiedDate: string | null) => {
    // Patch de LMD synchroon zodat een directe vervolg-save niet stale is —
    // de async refetchOrder kan tegelijk doorgaan om de rest van de order
    // (items, totalen, status) te verversen.
    if (nieuweLastModifiedDate) {
      setOrder((prev) => (prev ? { ...prev, lastModifiedDate: nieuweLastModifiedDate } : prev));
    }
    refetchOrder();
    setActivityRefreshKey((k) => k + 1);
  };

  const enableConfirmations = async () => {
    if (!order) return;
    setEnablingConfirmations(true);
    setConfirmationsError(null);
    try {
      const result = await setOrderConfirmations(order.id, true, order.lastModifiedDate);
      handleSaved(result.nieuweLastModifiedDate);
    } catch (err) {
      setConfirmationsError(err instanceof Error ? err.message : 'Activeren mislukt');
    } finally {
      setEnablingConfirmations(false);
    }
  };

  // "+ Extra ophaalrit inplannen". Voor een order die nog in een ophaal-planning-
  // status staat (deels opgehaald): open direct de plan-dialog. Voor een al
  // afgeronde ophaal ('Opgehaald', maar klant blijkt extra kleden te hebben):
  // heropen de ophaal — status terug naar 'Ophalen plannen' + 'Ophalen handmatig'
  // aan (onderdrukt auto-voorstel + reminders) — en open dan de plan-dialog. De
  // uitgevoerde rit blijft staan; nieuwe items (Status=null) worden bij
  // ritafronding per-kleed gestempeld door completeGeplandeRit.
  const handlePlanExtraPickup = async () => {
    if (!order) return;
    if (!reopenForExtraPickup) {
      openForPickup(order, refetchOrder);
      return;
    }
    setPlanningExtraPickup(true);
    setExtraPickupError(null);
    try {
      const result = await updateOrderFields(
        order.id,
        { newStatus: 'Ophalen plannen', flags: { ophalenHandmatigInplannen: true } },
        order.lastModifiedDate
      );
      const reopened: OrderDetail = {
        ...order,
        status: 'Ophalen plannen',
        lastModifiedDate: result.nieuweLastModifiedDate ?? order.lastModifiedDate,
      };
      openForPickup(reopened, refetchOrder);
      handleSaved(result.nieuweLastModifiedDate);
    } catch (err) {
      setExtraPickupError(
        err instanceof OrderConflictError
          ? 'Order is ondertussen gewijzigd. Herlaad en probeer opnieuw.'
          : err instanceof Error
            ? err.message
            : 'Heropenen mislukt'
      );
    } finally {
      setPlanningExtraPickup(false);
    }
  };

  const handleNotesSaved = (
    which: 'pickup' | 'delivery',
    nieuweLastModifiedDate: string | null,
    newValue: string
  ) => {
    // Optimistisch patchen zodat de UI direct klopt zonder full refetch te hoeven
    // afwachten. refetchOrder triggert daarna alsnog voor zekerheid.
    setOrder((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        lastModifiedDate: nieuweLastModifiedDate ?? prev.lastModifiedDate,
        notes: { ...prev.notes, [which]: newValue },
      };
    });
    refetchOrder();
    setActivityRefreshKey((k) => k + 1);
  };

  const pickupOverride = order.addresses.pickup.override?.enabled === true;
  const deliveryOverride = order.addresses.delivery.override?.enabled === true;

  const pickupCard = (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          {sofasaver ? 'Retour ophalen' : 'Ophalen'}
        </h2>
        <div className="flex items-center gap-2">
          {pickupOverride && (
            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-800">
              Ander adres actief
            </span>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => setAddressDialogSide('pickup')}
              className="text-xs text-slate-500 hover:text-slate-900 underline"
            >
              Ander adres
            </button>
          )}
        </div>
      </div>
      {order.addresses.pickup.formatted ? (
        <div className="text-sm text-slate-900 mb-3 leading-snug">
          {renderAddress(order.addresses.pickup.formatted)}
        </div>
      ) : (
        <p className="text-sm text-slate-400 mb-3">Geen ophaaladres bekend</p>
      )}
      <div className="mb-3">
        <p
          className="text-xs font-medium text-slate-500 mb-1 cursor-help"
          title="Vroegst mogelijke ophaaldatum — eerder doet het systeem geen ophaalvoorstel aan de klant"
        >
          Niet ophalen vóór
        </p>
        {order.pickupVanafDatum ? (
          <div className="flex items-center gap-2">
            <p className="text-sm text-slate-900">{formatDate(order.pickupVanafDatum)}</p>
            {canEdit && (
              <button
                type="button"
                onClick={() => setDatumDialogSide('pickup')}
                className="text-xs text-indigo-600 hover:underline"
              >
                Wijzigen
              </button>
            )}
          </div>
        ) : (
          canEdit && (
            <button
              type="button"
              onClick={() => setDatumDialogSide('pickup')}
              className="text-xs text-slate-500 hover:text-slate-900 underline"
            >
              + Datum instellen
            </button>
          )
        )}
      </div>
      <div className="mb-3">
        <p className="text-xs font-medium text-slate-500 mb-1">Afspraak</p>
        <AppointmentBlock
          appointment={order.pickupAppointment}
          canPlan={canSchedule}
          canForceCancel={canForceCancel}
          allowExtraPickup={allowExtraPickup}
          extraPickupBusy={planningExtraPickup}
          onPlan={handlePlanExtraPickup}
          onCancelAppointment={
            order.pickupAppointment?.ritId
              ? (recoveryMode) =>
                  setCancelAppointmentTarget({
                    ritId: order.pickupAppointment!.ritId!,
                    fase: 'pickup',
                    datum: order.pickupAppointment!.vanaf,
                    recoveryMode,
                  })
              : undefined
          }
        />
        {extraPickupError && (
          <p className="mt-1 text-xs text-red-600">{extraPickupError}</p>
        )}
        <FailedAttemptNote
          attempt={order.pickupLastFailedAttempt}
          fase="pickup"
          canUndo={canForceCancel}
          onUndone={() => {
            refetchOrder();
            setActivityRefreshKey((k) => k + 1);
          }}
        />
        <SendEtaAction order={order} kind="ophaal_eta" />
        {canMarkPickedUp && (
          <button
            type="button"
            onClick={() => setShowMarkPickedUp(true)}
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 bg-emerald-600 text-white rounded hover:bg-emerald-700"
            title="Kleed is al binnen zonder ophaalrit (chauffeur nam het mee, of klant bracht het zelf). Markeer als binnen — gaat direct naar voorinspectie."
          >
            Kleed al binnen (geen rit)
          </button>
        )}
        {canNaOphaal && (
          <button
            type="button"
            onClick={() => setShowNalevering('ophalen')}
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 bg-amber-600 text-white rounded hover:bg-amber-700"
            title="Een kleed is niet meegenomen terwijl de stop als klaar is gemeld. Zet het terug naar vóór-ophaal en heropen de order voor een nieuwe ophaalrit (geen klantmail)."
          >
            Niet opgehaald, na-ophaal plannen
          </button>
        )}
      </div>
      {canEditNotesForOrder ? (
        <EditableNotes
          orderId={order.id}
          field="pickupNotes"
          value={order.notes.pickup}
          expectedLastModifiedDate={order.lastModifiedDate}
          canEdit={canEditNotesForOrder}
          onSaved={(lmd, newValue) => handleNotesSaved('pickup', lmd, newValue)}
        />
      ) : order.notes.pickup ? (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs font-medium text-slate-500 mb-1">Opmerkingen</p>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{order.notes.pickup}</p>
        </div>
      ) : null}
    </div>
  );

  const deliveryCard = (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
          {sofasaver ? 'Bezorgen' : 'Afleveren'}
        </h2>
        <div className="flex items-center gap-2">
          {deliveryOverride && (
            <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-800">
              Ander adres actief
            </span>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => setAddressDialogSide('delivery')}
              className="text-xs text-slate-500 hover:text-slate-900 underline"
            >
              Ander adres
            </button>
          )}
        </div>
      </div>
      {order.addresses.delivery.formatted ? (
        <div className="text-sm text-slate-900 mb-3 leading-snug">
          {renderAddress(order.addresses.delivery.formatted)}
        </div>
      ) : (
        <p className="text-sm text-slate-400 mb-3">Geen afleveradres bekend</p>
      )}
      <div className="mb-3">
        <p
          className="text-xs font-medium text-slate-500 mb-1 cursor-help"
          title="Vroegst mogelijke afleverdatum — eerder doet het systeem geen aflevervoorstel aan de klant"
        >
          Niet afleveren vóór
        </p>
        {order.deliveryVanafDatum ? (
          <div className="flex items-center gap-2">
            <p className="text-sm text-slate-900">{formatDate(order.deliveryVanafDatum)}</p>
            {canEdit && (
              <button
                type="button"
                onClick={() => setDatumDialogSide('delivery')}
                className="text-xs text-indigo-600 hover:underline"
              >
                Wijzigen
              </button>
            )}
          </div>
        ) : (
          canEdit && (
            <button
              type="button"
              onClick={() => setDatumDialogSide('delivery')}
              className="text-xs text-slate-500 hover:text-slate-900 underline"
            >
              + Datum instellen
            </button>
          )
        )}
      </div>
      <div className="mb-3">
        <p className="text-xs font-medium text-slate-500 mb-1">Afspraak</p>
        <AppointmentBlock
          appointment={order.deliveryAppointment}
          canPlan={canSchedule}
          canForceCancel={canForceCancel}
          allowExtraPickup={allowExtraDelivery}
          extraRitLabel="+ Extra afleverrit inplannen"
          extraRitTitle="Plan een nieuwe afleverrit voor een kleed dat nog bij ons ligt (de uitgevoerde rit blijft staan)"
          onPlan={() => openForDelivery(order, refetchOrder)}
          onCancelAppointment={
            order.deliveryAppointment?.ritId
              ? (recoveryMode) =>
                  setCancelAppointmentTarget({
                    ritId: order.deliveryAppointment!.ritId!,
                    fase: 'delivery',
                    datum: order.deliveryAppointment!.vanaf,
                    recoveryMode,
                  })
              : undefined
          }
        />
        <FailedAttemptNote
          attempt={order.deliveryLastFailedAttempt}
          fase="delivery"
          canUndo={canForceCancel}
          onUndone={() => {
            refetchOrder();
            setActivityRefreshKey((k) => k + 1);
          }}
        />
        <SendEtaAction order={order} kind="aflever_eta" />
        <SendEtaAction order={order} kind="klaar" />
        {canNalevering && (
          <button
            type="button"
            onClick={() => setShowNalevering('afleveren')}
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 bg-amber-600 text-white rounded hover:bg-amber-700"
            title="Een kleed is niet afgeleverd terwijl de stop als klaar is gemeld. Neem het terug naar 'In bus' en heropen de order voor een nieuwe afleverrit (geen klantmail)."
          >
            Niet afgeleverd, nalevering plannen
          </button>
        )}
      </div>
      {canEditNotesForOrder ? (
        <EditableNotes
          orderId={order.id}
          field="deliveryNotes"
          value={order.notes.delivery}
          expectedLastModifiedDate={order.lastModifiedDate}
          canEdit={canEditNotesForOrder}
          onSaved={(lmd, newValue) => handleNotesSaved('delivery', lmd, newValue)}
        />
      ) : order.notes.delivery ? (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs font-medium text-slate-500 mb-1">Opmerkingen</p>
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{order.notes.delivery}</p>
        </div>
      ) : null}
    </div>
  );

  // Zelf brengen/halen vervangt de betreffende sectie: ophalen en afleveren gaan
  // over ritten, adressen en afspraken, en daar is bij een walk-in niets van van
  // toepassing. De zelfservice-kaart neemt de plek in en draagt de registratie.
  const pickupSlot = order.selfDelivery ? (
    <SelfServiceCompleteCard order={order} side="brengen" onCompleted={refetchOrder} />
  ) : (
    pickupCard
  );
  const deliverySlot = order.selfPickup ? (
    <SelfServiceCompleteCard order={order} side="halen" onCompleted={refetchOrder} />
  ) : (
    deliveryCard
  );

  // Actielinks (factuur, terugbetaling, klantpagina, …): één definitie voor
  // de pagina én de drawer (Willem 14-09: drawers = volledige weergave).
  const headerActies = (
      <div className="flex items-center gap-3 mt-2">
        <button
          type="button"
          onClick={() => setShowSendEmailDialog(true)}
          disabled={order.emailMagVerzonden === false}
          title={
            order.emailMagVerzonden === false
              ? 'Contact heeft geen geldig e-mailadres of is bounced'
              : undefined
          }
          className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 disabled:text-slate-300 disabled:cursor-not-allowed"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Stuur e-mail
        </button>
        {isWasserijOrder(order) && (
          <button
            type="button"
            onClick={handleOpenRepeatDialog}
            disabled={repeatLoading}
            title="Maak een nieuwe offerte voor dezelfde klant op basis van deze order"
            className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 disabled:text-slate-300"
          >
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {repeatLoading ? 'Laden…' : 'Vervolgorder maken'}
          </button>
        )}
        {isWasserijOrder(order) &&
          !order.totals.gefactureerd &&
          !['Geannuleerd', 'Terugbetaald'].includes(order.status) && (
            <button
              type="button"
              onClick={() => setShowCreateInvoiceDialog(true)}
              className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Factuur maken
            </button>
          )}
        {order.invoice && (
          <Link
            to={`/facturen/${order.invoice.id}`}
            className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
          >
            Bekijk factuur ({order.invoice.name})
          </Link>
        )}
        {/* B2B-cyclusklant (Atlas): A4 met hun inkoopnummer voor op de zak
            die met de klant mee retour gaat. Bewust hier en niet alleen in
            de wizard — dit is de plek waar je een document bij een order zoekt. */}
        {isB2BCyclusAccount(order.customer.accountId) && (
          <B2BRetourlabelButton
            werkOrderId={order.id}
            orderReferentie={order.orderNumber}
            className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
          />
        )}
        {isWasserijOrder(order) &&
          order.totals.gefactureerd &&
          order.invoice?.fullyCredited &&
          !['Geannuleerd', 'Terugbetaald'].includes(order.status) && (
            <button
              type="button"
              onClick={() => setShowReleaseInvoiceDialog(true)}
              title="De factuur is volledig gecrediteerd — geef de order vrij zodat er een nieuwe factuur uit kan"
              className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Opnieuw factureren
            </button>
          )}
        {webshop &&
          order.totals.totalInclBtw > 0 &&
          !order.totals.paymentReceived &&
          !['Geannuleerd', 'Terugbetaald'].includes(order.status) && (
            <button
              type="button"
              onClick={() => setShowMaakGratisDialog(true)}
              title="Order alsnog gratis maken (100% korting op alle regels) — vangnet voor een per ongeluk doorberekende nalevering"
              className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Maak gratis
            </button>
          )}
        <button
          type="button"
          onClick={() => setShowRefundDialog(true)}
          title="Betaald bedrag (deels) terugbetalen aan de klant"
          className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 6h18M3 14h18M3 18h18" />
          </svg>
          Terugbetaling
        </button>
        {order.clientAppToken && (
          <a
            href={`https://client.kleda.nl/?token=${encodeURIComponent(order.clientAppToken)}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Open de persoonlijke orderpagina zoals de klant die ziet"
            className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
          >
            <ExternalLinkIcon />
            Klantpagina bekijken
          </a>
        )}
      </div>
  );

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Drawer sticky header */}
      {isDrawer && (
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            aria-label="Sluiten"
            className="text-slate-400 hover:text-slate-600"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h2 className="text-sm font-semibold text-slate-900 truncate">{order.orderNumber || 'Order'}</h2>
          {order.status && (
            <span className={clsx(
              'text-xs px-2 py-0.5 rounded font-medium flex-shrink-0',
              ORDER_STATUS_COLORS[order.status] || 'bg-slate-100 text-slate-600'
            )}>
              {order.statusLabel || order.status}
            </span>
          )}
          <Link
            to={`/orders/${order.id}`}
            onClick={onClose}
            className="ml-auto text-xs text-indigo-600 hover:underline flex-shrink-0"
          >
            Open volledig →
          </Link>
        </div>
      )}

      {/* Page header (with back-link) */}
      {!isDrawer && (
        <div className="px-6 py-4 border-b border-slate-200 bg-white">
          {order.customer.accountId ? (
            <Link
              to={`/klanten/${order.customer.accountId}`}
              className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Terug naar klant
            </Link>
          ) : (
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Terug
            </button>
          )}

          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h1 className="text-xl font-semibold text-slate-900">{order.orderNumber || 'Order'}</h1>
                {order.status && (
                  <StatusEditPopover
                    orderId={order.id}
                    currentStatus={order.status}
                    currentStatusLabel={order.statusLabel || order.status}
                    availableStatuses={order.availableStatuses ?? []}
                    expectedLastModifiedDate={order.lastModifiedDate}
                    canEdit={canEdit}
                    onSaved={handleSaved}
                  />
                )}
                {order.brand && (
                  <span className={clsx(
                    'text-xs px-2 py-0.5 rounded font-medium',
                    BRAND_COLORS[order.brand] || 'bg-slate-100 text-slate-600'
                  )}>
                    {order.brand === 'SCHOONHUISSHOP' ? 'SHS' : order.brand}
                  </span>
                )}
                {order.segment && (
                  <span className="text-xs px-2 py-0.5 rounded font-medium bg-slate-100 text-slate-600">
                    {order.segment}
                  </span>
                )}
                {order.klachtParentId && order.klachtParentNummer && (
                  <KlachtBadge
                    klachtId={order.klachtParentId}
                    klachtNummer={order.klachtParentNummer}
                    status={order.klachtStatus ?? undefined}
                  />
                )}
                {order.isVervolgWo && (
                  <VervolgWoBadge
                    klachtId={order.klachtParentId ?? undefined}
                    klachtNummer={order.klachtParentNummer ?? undefined}
                  />
                )}
              </div>
              {headerActies}
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-2xl font-semibold text-slate-900">
                {formatCurrency(order.totals.totalInclBtw)}
              </div>
              {order.totals.paymentReceived ? (
                <span className="text-xs text-emerald-600 flex items-center gap-1 justify-end mt-0.5">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Betaald
                </span>
              ) : order.totals.openAmount > 0 ? (
                <span className="text-xs text-amber-600 mt-0.5 block">
                  Openstaand: {formatCurrency(order.totals.openAmount)}
                </span>
              ) : null}
              <div className="mt-1.5 flex flex-wrap justify-end gap-1.5">
                <BundelkortingToggle
                  recordId={order.id}
                  geenBundelkorting={order.geenBundelkorting === true}
                  canEdit={canEdit}
                  onSaved={refetchOrder}
                />
                <MarketingContextChip accountId={order.customer.accountId} />
                {order.discount && (
                  <KortingPill
                    orderId={order.id}
                    discount={order.discount}
                    gefactureerd={order.totals.gefactureerd === true}
                    canEdit={canEdit}
                    onSaved={handleSaved}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Drawer header summary (compact) */}
      {isDrawer && (
        <div className="px-4 py-3 border-b border-slate-200 bg-white flex items-start justify-between gap-3 flex-shrink-0">
          <div className="min-w-0 flex items-center gap-2 flex-wrap">
            {order.brand && (
              <span className={clsx(
                'text-xs px-2 py-0.5 rounded font-medium',
                BRAND_COLORS[order.brand] || 'bg-slate-100 text-slate-600'
              )}>
                {order.brand === 'SCHOONHUISSHOP' ? 'SHS' : order.brand}
              </span>
            )}
            {order.segment && (
              <span className="text-xs px-2 py-0.5 rounded font-medium bg-slate-100 text-slate-600">
                {order.segment}
              </span>
            )}
            {order.klachtParentId && order.klachtParentNummer && (
              <KlachtBadge
                klachtId={order.klachtParentId}
                klachtNummer={order.klachtParentNummer}
                status={order.klachtStatus ?? undefined}
                size="sm"
              />
            )}
            {order.isVervolgWo && (
              <VervolgWoBadge
                klachtId={order.klachtParentId ?? undefined}
                klachtNummer={order.klachtParentNummer ?? undefined}
                size="sm"
              />
            )}
            {order.invoice && (
              <Link
                to={`/facturen/${order.invoice.id}`}
                onClick={() => { if (isDrawer && onClose) onClose(); }}
                className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
              >
                Factuur ({order.invoice.name})
              </Link>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-lg font-semibold text-slate-900">
              {formatCurrency(order.totals.totalInclBtw)}
            </div>
            {order.totals.paymentReceived ? (
              <span className="text-[11px] text-emerald-600">Betaald</span>
            ) : order.totals.openAmount > 0 ? (
              <span className="text-[11px] text-amber-600">
                Open: {formatCurrency(order.totals.openAmount)}
              </span>
            ) : null}
            {order.discount && (
              <div className="mt-1 flex flex-wrap justify-end gap-1.5">
                <MarketingContextChip accountId={order.customer.accountId} />
                <KortingPill
                  orderId={order.id}
                  discount={order.discount}
                  gefactureerd={order.totals.gefactureerd === true}
                  canEdit={canEdit}
                  onSaved={handleSaved}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {isDrawer && (
        <div className="px-4 py-2 border-b border-slate-200 bg-white flex-shrink-0 [&>div]:mt-0 [&>div]:flex-wrap">
          {headerActies}
        </div>
      )}

      {/* Body */}
      <div className={clsx('flex-1 overflow-y-auto space-y-6', isDrawer ? 'p-4' : 'p-6')}>
        {/* Bevestigingen-uit banner */}
        {order.confirmationsEnabled === false && (
          <div className="flex flex-col gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-900">
            <div className="flex items-center justify-between gap-3">
              <span>
                <strong>Bevestigingen uit.</strong> Klant heeft nog geen opdrachtbevestiging ontvangen.
              </span>
              <button
                type="button"
                onClick={enableConfirmations}
                disabled={enablingConfirmations}
                className="px-3 py-1.5 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-60 text-xs font-medium whitespace-nowrap"
              >
                {enablingConfirmations ? 'Bezig…' : 'Nu versturen'}
              </button>
            </div>
            {confirmationsError && (
              <p className="text-xs text-red-700">⚠ {confirmationsError}</p>
            )}
          </div>
        )}

        {/* Top grid: customer + payment */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Klant</h2>
            {order.customer.accountId ? (
              <Link
                to={`/klanten/${order.customer.accountId}`}
                className="text-sm font-medium text-indigo-600 hover:underline block"
              >
                {order.customer.name || '(geen naam)'}
              </Link>
            ) : (
              <p className="text-sm font-medium text-slate-900">{order.customer.name || '(geen klant gekoppeld)'}</p>
            )}
            <div className="text-xs text-slate-500 mt-2 space-y-1">
              {order.customer.email && (
                <a href={`mailto:${order.customer.email}`} className="block hover:text-indigo-600 truncate">
                  {order.customer.email}
                </a>
              )}
              {order.customer.phone && (
                <div>
                  <CallButton
                    number={order.customer.phone}
                    brand={(order.brand as Brand) || undefined}
                    customerId={order.customer.accountId ?? undefined}
                    customerName={order.customer.name ?? undefined}
                    sourceContext="order-detail"
                    variant="inline"
                  >
                    {order.customer.phone}
                  </CallButton>
                </div>
              )}
              {order.customer.mobile && order.customer.mobile !== order.customer.phone && (
                <div>
                  <CallButton
                    number={order.customer.mobile}
                    brand={(order.brand as Brand) || undefined}
                    customerId={order.customer.accountId ?? undefined}
                    customerName={order.customer.name ?? undefined}
                    sourceContext="order-detail"
                    variant="inline"
                  >
                    {order.customer.mobile} (mobiel)
                  </CallButton>
                </div>
              )}
            </div>
            {(() => {
              const canEditContact =
                canEdit && !!order.customer.accountId && !order.customer.isPersonAccount;
              const contact = order.customer.contact;
              // Zelfde persoon als de klant? Toon dan één compacte regel i.p.v.
              // dezelfde naam/e-mail dubbel onder elkaar.
              const sameAsCustomer =
                !!contact &&
                (contact.name ?? '') === (order.customer.name ?? '') &&
                (contact.email ?? '') === (order.customer.email ?? '');
              if (!contact && !canEditContact) return null;
              if (sameAsCustomer && !canEditContact) return null;
              return (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                      Contactpersoon
                    </p>
                    {canEditContact && (
                      <button
                        type="button"
                        onClick={() => setShowContactDialog(true)}
                        className="text-xs text-indigo-600 hover:underline"
                      >
                        {contact ? 'Wijzigen' : 'Kiezen'}
                      </button>
                    )}
                  </div>
                  {contact ? (
                    sameAsCustomer ? (
                      <p className="text-xs text-slate-500">Zelfde als klant</p>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-slate-900">
                          {contact.name || '(geen naam)'}
                          {contact.title && (
                            <span className="text-xs font-normal text-slate-500"> — {contact.title}</span>
                          )}
                        </p>
                        <div className="text-xs text-slate-500 mt-1 space-y-1">
                          {contact.email && (
                            <a href={`mailto:${contact.email}`} className="block hover:text-indigo-600 truncate">
                              {contact.email}
                            </a>
                          )}
                          {contact.phone && (
                            <PhoneNumber
                              number={contact.phone}
                              brand={(order.brand as Brand) || undefined}
                              sourceContext="order-detail-contact"
                              className="block hover:text-indigo-600"
                            />
                          )}
                          {contact.mobile && contact.mobile !== contact.phone && (
                            <PhoneNumber
                              number={contact.mobile}
                              display={`${contact.mobile} (mobiel)`}
                              brand={(order.brand as Brand) || undefined}
                              sourceContext="order-detail-contact"
                              className="block hover:text-indigo-600"
                            />
                          )}
                        </div>
                      </>
                    )
                  ) : (
                    <p className="text-xs text-slate-400 italic">Geen contactpersoon gekozen</p>
                  )}
                </div>
              );
            })()}

            {(() => {
              const factuur = order.factuur;
              // Toon de regel als er een afwijkende naam/opmerking/e-mail staat, of als je mag bewerken.
              const hasOverride = !!factuur && (factuur.andereNaam || !!factuur.opmerkingen || !!factuur.email);
              if (!hasOverride && !canEdit) return null;
              return (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                      Naam op factuur
                    </p>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => setShowInvoiceNameDialog(true)}
                        className="text-xs text-indigo-600 hover:underline"
                      >
                        Wijzigen
                      </button>
                    )}
                  </div>
                  {factuur?.andereNaam ? (
                    <p className="text-sm font-medium text-slate-900 flex items-center gap-2">
                      <span className="truncate">{factuur.naam || '(geen naam)'}</span>
                      <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800">
                        afwijkend
                      </span>
                    </p>
                  ) : (
                    <p className="text-sm text-slate-600">
                      {order.customer.name || '(geen naam)'}
                      <span className="text-xs text-slate-400"> · standaard</span>
                    </p>
                  )}
                  {factuur?.email && (
                    <p className="text-xs text-slate-600 mt-1 flex items-center gap-2 min-w-0">
                      <span className="truncate">{factuur.email}</span>
                      <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800">
                        factuur-e-mail
                      </span>
                    </p>
                  )}
                  {factuur?.opmerkingen && (
                    <p className="text-xs text-slate-500 mt-1 whitespace-pre-wrap break-words">
                      {factuur.opmerkingen}
                    </p>
                  )}
                </div>
              );
            })()}
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              {isDrawer ? 'Status & betaling' : 'Betaling'}
            </h2>
            <div className="text-sm space-y-1">
              {/* Op de volledige pagina staat de status al bewerkbaar in de
                  header — alleen de drawer heeft deze regel nodig. */}
              {isDrawer && (
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Status</span>
                  <StatusEditPopover
                    orderId={order.id}
                    currentStatus={order.status}
                    currentStatusLabel={order.statusLabel || order.status || '—'}
                    availableStatuses={order.availableStatuses ?? []}
                    expectedLastModifiedDate={order.lastModifiedDate}
                    canEdit={canEdit}
                    onSaved={handleSaved}
                  />
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-500">Totaal incl. BTW</span>
                <span className="text-slate-900">{formatCurrency(order.totals.totalInclBtw)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Betaald</span>
                <span className="text-slate-900">{formatCurrency(order.totals.paidInclBtw)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Openstaand</span>
                <span className={clsx(order.totals.openAmount > 0 ? 'text-amber-600' : 'text-slate-900')}>
                  {formatCurrency(order.totals.openAmount)}
                </span>
              </div>
              {order.totals.paymentMethod && (
                <div className="flex justify-between">
                  <span className="text-slate-500">Betaalmethode</span>
                  <span className="text-slate-900">{order.totals.paymentMethod}</span>
                </div>
              )}
              {order.totals.molliePaymentUrl && !order.totals.paymentReceived && !order.totals.opFactuur && (
                <div className="flex justify-between items-center pt-1">
                  <span className="text-slate-500">Mollie</span>
                  <a
                    href={order.totals.molliePaymentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-700"
                  >
                    <ExternalLinkIcon />
                    Open betaallink
                  </a>
                </div>
              )}
              {order.totals.molliePaymentUrl && !order.totals.paymentReceived && !order.totals.opFactuur && (
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={handleSyncMollie}
                    disabled={syncingMollie}
                    className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
                    title="Controleer bij Mollie of deze order al betaald is en boek de betaling direct. Vangnet voor een gemiste webhook."
                  >
                    {syncingMollie ? 'Bezig met Mollie…' : 'Sync vanuit Mollie'}
                  </button>
                  {syncMollieMsg && (
                    <p
                      className={clsx(
                        'text-xs mt-1',
                        syncMollieMsg.kind === 'ok' && 'text-emerald-600',
                        syncMollieMsg.kind === 'error' && 'text-red-600',
                        syncMollieMsg.kind === 'info' && 'text-slate-500'
                      )}
                    >
                      {syncMollieMsg.text}
                    </p>
                  )}
                </div>
              )}
              {wasserij && (
                <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                  <span
                    className="text-slate-500 cursor-help"
                    title="Klant betaalt achteraf per factuur in plaats van vooraf via een betaallink"
                  >
                    Op factuur achteraf
                  </span>
                  <div className="flex items-center gap-2">
                    {order.totals.opFactuur && (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800"
                        title={order.totals.opFactuurBron === 'Brein-Auto'
                          ? 'Automatisch: deze klant betaalt altijd op rekening (klantkaart)'
                          : order.totals.opFactuurBron ? `Gezet via ${order.totals.opFactuurBron}` : undefined}
                      >
                        {order.totals.opFactuurBron === 'Brein-Auto' ? 'Ja · klant op rekening' : 'Ja'}
                      </span>
                    )}
                    {canEdit && !order.totals.paymentReceived && (
                      <button
                        type="button"
                        onClick={() => setShowOpFactuurDialog(true)}
                        className="text-xs text-indigo-600 hover:text-indigo-700 underline"
                      >
                        {order.totals.opFactuur ? 'Terug naar standaard' : 'Zet op factuur'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* Operationele vlaggen — compacte pill-rij; altijd zichtbaar zodat
                Spoed direct te zetten is tijdens telefoontjes. */}
            {canEdit && <OrderFlagsRow order={order} onSaved={handleSaved} />}
          </div>
        </div>

        {/* Mid: webshop = single shipping card; otherwise pickup + delivery grid */}
        {webshop ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Verzendadres</h2>
            {order.addresses.shipping.formatted ? (
              <div className="text-sm text-slate-900 leading-snug">
                {renderAddress(order.addresses.shipping.formatted)}
              </div>
            ) : (
              <p className="text-sm text-slate-400">Geen verzendadres bekend</p>
            )}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {sofasaver ? (
              <>
                {deliverySlot}
                {pickupSlot}
              </>
            ) : (
              <>
                {pickupSlot}
                {deliverySlot}
              </>
            )}
          </div>
        )}

        {/* Voorinspectie-rapport quick-link — opent de klant-view in een nieuw
            tabblad zodat office snel kan zien wat de klant ziet. Alleen voor
            Wasserij-orders en als er minstens één kleed met bevindingen is. */}
        {wasserij && order.clientAppToken && order.items.some(
          (g) => g.primary.voorinspectie.length > 0 || g.primary.voorinspectieOpmerkingen
        ) && (
          <a
            href={`https://client.kleda.nl/?token=${encodeURIComponent(order.clientAppToken)}&view=voorinspectie`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-900 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100"
          >
            🔍 Voorinspectie-rapport openen (klant-view) ↗
          </a>
        )}

        {/* Opslag-stand: dezelfde rekenregel als de cron en de agents */}
        <OpslagStandBadge opslag={opslag} />

        {/* Items */}
        {canEdit ? (
          <>
            {extraRemoveError && (
              <div className="px-4 py-2 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">
                {extraRemoveError}
              </div>
            )}
            <EditableOrderItems
              order={order}
              onSaved={handleSaved}
              onItemDeleted={handleSaved}
              onIntentChanged={() => handleSaved(null)}
              onEditItem={setEditingItem}
              onRemovePaidExtra={handleRemovePaidExtra}
              onKortingSaved={handleSaved}
              onAddBehandeling={
                wasserij
                  ? (itemId, name) => setBehandelingTarget({ itemId, name })
                  : undefined
              }
              onAddFee={wasserij ? () => setShowFeeDialog(true) : undefined}
              onAddKleed={wasserij ? () => setShowAddKleedDialog(true) : undefined}
            />
          </>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="px-6 py-3 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-900">
                Items ({order.items.length})
              </h2>
            </div>
            {order.items.length === 0 ? (
              <p className="px-6 py-6 text-sm text-slate-400 text-center">Geen items</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {order.items.map((group) => (
                  <ItemGroup
                    key={group.primary.id}
                    group={group}
                    webshop={webshop}
                    clickable={wasserij}
                  />
                ))}
              </div>
            )}
            <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 rounded-b-xl">
              <div className="flex justify-between text-xs text-slate-500">
                <span>Totaal excl. BTW (geschat)</span>
                <span>{formatCurrency(totalsExcl)}</span>
              </div>
              <div className="flex justify-between text-sm font-medium text-slate-900 mt-1">
                <span>Totaal incl. BTW</span>
                <span>{formatCurrency(order.totals.totalInclBtw)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Transacties — altijd zichtbaar op orders zodat kantoor handmatig kan registreren */}
        <TransactiesSection
          parentType="Werk_Order__c"
          parentId={order.id}
          transacties={order.transacties || []}
          openAmount={order.totals.openAmount}
          onMutated={refetchOrder}
        />

        <RefundsSection werkOrderId={order.id} refreshKey={refundsRefreshKey} />

        {/* Upsells — alleen voor Kleda/Wasserij-orders */}
        {wasserij && canEdit && (
          <UpsellSection order={order} onRefresh={refetchOrder} />
        )}

        {/* Reparaties — Kleda/Wasserij-orders */}
        {wasserij && canEdit && <ReparatieSection order={order} />}

        {/* Eigen media op WO-niveau (chauffeur-referentiefoto's e.d.) —
            verbergt zichzelf als er niets is (media-laag fase 2) */}
        <MediaGrid woSfId={order.id} cardTitle="Media (eigen laag)" />

        {/* Opmerkingen voor planning — append-style audit-log, alle order-types */}
        <PlanningNotesSection
          orderId={order.id}
          value={order.notes.planning}
          onAppended={(newValue) =>
            setOrder((prev) =>
              prev ? { ...prev, notes: { ...prev.notes, planning: newValue } } : prev
            )
          }
        />

        <OphaalReminderCard order={order} onReactivated={refetchOrder} />

        {/* Reminder-pauze (klant op vakantie): pauzeer aflever/ophaal-reminders
            t/m een datum. Verschijnt zodra de WO in een aflever- of ophaal-sequence
            zit óf al een pauze heeft. Alleen admin/medewerker (zelfde gate als de
            snooze-proxy). */}
        {order.reminderSnooze &&
          (role === 'admin' || role === 'medewerker') &&
          (order.reminderSnooze.inAfleverSequence ||
            order.reminderSnooze.inOphaalSequence ||
            order.reminderSnooze.afleverPauzeTot ||
            order.reminderSnooze.ophaalPauzeTot) && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                  Reminder-pauze
                </h2>
                <button
                  type="button"
                  onClick={() => setShowSnoozeDialog(true)}
                  className="px-3 py-1.5 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                >
                  Reminders pauzeren tot…
                </button>
              </div>
              {order.reminderSnooze.afleverPauzeTot || order.reminderSnooze.ophaalPauzeTot ? (
                <div className="flex flex-col gap-1.5">
                  {order.reminderSnooze.afleverPauzeTot && (
                    <span className="inline-flex items-center gap-1.5 self-start text-xs px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800">
                      ⏸ Aflever-reminders gepauzeerd t/m {formatDate(order.reminderSnooze.afleverPauzeTot)}
                    </span>
                  )}
                  {order.reminderSnooze.ophaalPauzeTot && (
                    <span className="inline-flex items-center gap-1.5 self-start text-xs px-2 py-1 rounded bg-amber-50 border border-amber-200 text-amber-800">
                      ⏸ Ophaal-reminders gepauzeerd t/m {formatDate(order.reminderSnooze.ophaalPauzeTot)}
                    </span>
                  )}
                </div>
              ) : (
                <p className="text-sm text-slate-600">
                  Geen pauze actief. Pauzeer de reminders als de klant op vakantie is of
                  pas later op-/afgeleverd wil worden.
                </p>
              )}
            </div>
          )}

        {/* Herstel ("un-cancel") — alleen Wasserij + admin/medewerker. De kaart
            verbergt zichzelf als de order niet geannuleerd is (of de
            reminder-reden heeft, die door OphaalReminderCard wordt afgehandeld). */}
        {wasserij && (role === 'admin' || role === 'medewerker') && (
          <RestoreOrderCard order={order} onRestored={refetchOrder} />
        )}

        {/* Klacht-sectie: register-knop + summary-card. Klacht-flow zit
           bewust niet in Danger zone — registreren is een normale actie. */}
        {(canRegisterNewKlacht || hasOpenKlacht || klachtenOpOrder.length > 0) && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                Klacht{openKlachtenOpOrder.length > 0 ? ` (${openKlachtenOpOrder.length} open)` : ''}
              </h2>
              {canRegisterNewKlacht && (
                <button
                  type="button"
                  onClick={() =>
                    openKlachtDialog('register', {
                      werkOrderId: order.id,
                      werkOrderNummer: order.orderNumber,
                    })
                  }
                  className="px-3 py-1.5 text-sm font-medium text-red-700 border border-red-300 rounded hover:bg-red-50"
                >
                  ⚠ Klacht registreren
                </button>
              )}
            </div>
            {hasOpenKlacht && order.klachtParentId && (
              <KlachtSummaryCard
                klachtId={order.klachtParentId}
                klachtNummer={order.klachtParentNummer}
                klachtStatus={order.klachtStatus}
              />
            )}
            {openKlachtenOpOrder.map((k) => (
              <KlachtSummaryCard
                key={k.id}
                klachtId={k.id}
                klachtNummer={k.nummer}
                klachtStatus={k.status}
              />
            ))}
            {openKlachtenOpOrder.length > 0 && (
              <p className="text-xs text-slate-500">
                Nieuwe melding over deze order? Zet hem als update op de bestaande klacht; een
                tweede registratie landt daar automatisch.
              </p>
            )}
            {geslotenKlachtenOpOrder.length > 0 && (
              <ul className="text-xs text-slate-500 space-y-0.5">
                {geslotenKlachtenOpOrder.map((k) => (
                  <li key={k.id}>
                    <Link to={`/klachten/${k.id}`} className="underline hover:text-slate-700">
                      {k.nummer}
                    </Link>{' '}
                    ({klachtStatusLabel(k.status)}
                    {k.datumGeopend ? `, ${formatDate(k.datumGeopend)}` : ''})
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Berichten — bidirectionele communicatielijn office↔uitvoering */}
        <BerichtThread
          werkOrderId={order.id}
          werkOrderName={order.orderNumber}
        />

        {/* Office activity feed — alleen voor Wasserij-orders (waar we ook muteren) */}
        {canEdit && (
          <ActivityFeed orderId={order.id} refreshKey={activityRefreshKey} />
        )}

        {/* System info */}
        <div className="text-xs text-slate-400 flex items-center gap-4 flex-wrap">
          <span>Aangemaakt op {formatDate(order.createdDate)}</span>
          <span>Ordernummer: {order.orderNumber}</span>
          {order.recordType && <span>Type: {order.recordType}</span>}
        </div>

        {/* Danger zone */}
        {(canCancel || canDelete) && (
          <div className="rounded-xl border border-red-200 bg-red-50/30 p-4">
            <h2 className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">
              Gevarenzone
            </h2>
            <div className="flex flex-wrap gap-2">
              {canCancel && (
                <button
                  type="button"
                  onClick={() => setShowCancelDialog(true)}
                  className="px-4 py-2 text-sm font-medium text-red-700 border border-red-300 rounded hover:bg-red-50"
                >
                  🗙 Order annuleren
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  onClick={() => setShowDeleteDialog(true)}
                  className="px-4 py-2 text-sm font-medium text-red-700 border border-red-300 rounded hover:bg-red-50"
                >
                  🗑 Order verwijderen
                </button>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Annuleren = klant ziet ervan af. Verwijderen = order had nooit moeten bestaan (dubbel/foutief).
            </p>
          </div>
        )}
      </div>

      <CancelOrderDialog
        order={order}
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onCancelled={handleSaved}
      />

      <DeleteOrderDialog
        order={order}
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onDeleted={handleDeleted}
      />

      <SendEmailDialog
        orderId={order.id}
        orderNumber={order.orderNumber}
        currentStatus={order.status}
        isOpen={showSendEmailDialog}
        onClose={() => setShowSendEmailDialog(false)}
        onSent={() => {
          setShowSendEmailDialog(false);
          setActivityRefreshKey((k) => k + 1);
        }}
      />

      <RefundDialog
        werkOrderId={order.id}
        orderNumber={order.orderNumber}
        isOpen={showRefundDialog}
        onClose={() => setShowRefundDialog(false)}
        onSuccess={() => {
          setShowRefundDialog(false);
          setRefundsRefreshKey((k) => k + 1);
          setActivityRefreshKey((k) => k + 1);
        }}
      />

      <MaakWebshopGratisDialog
        orderId={order.id}
        orderNumber={order.orderNumber}
        customerName={order.customer.name}
        totalInclBtw={order.totals.totalInclBtw}
        isOpen={showMaakGratisDialog}
        onClose={() => setShowMaakGratisDialog(false)}
        onSuccess={() => {
          refetchOrder();
          setActivityRefreshKey((k) => k + 1);
        }}
      />

      <OpFactuurToggleDialog
        order={order}
        isOpen={showOpFactuurDialog}
        onClose={() => setShowOpFactuurDialog(false)}
        onToggled={() => {
          refetchOrder();
          setActivityRefreshKey((k) => k + 1);
        }}
      />

      <CreateInvoiceDialog
        werkOrderId={order.id}
        orderNumber={order.orderNumber}
        itemGroups={order.items}
        totalInclBtw={order.totals.totalInclBtw}
        isOpen={showCreateInvoiceDialog}
        onClose={() => setShowCreateInvoiceDialog(false)}
        onSuccess={() => {
          refetchOrder();
          setActivityRefreshKey((k) => k + 1);
        }}
      />

      {order.invoice && (
        <ReleaseWerkOrderDialog
          isOpen={showReleaseInvoiceDialog}
          werkOrderId={order.id}
          orderNumber={order.orderNumber}
          invoiceName={order.invoice.name}
          nieuwTotaalInclBtw={order.totals.totalInclBtw}
          onClose={() => setShowReleaseInvoiceDialog(false)}
          onSuccess={() => {
            refetchOrder();
            setActivityRefreshKey((k) => k + 1);
          }}
        />
      )}

      {repeatPrefill && (
        <RepeatOrderDialog
          isOpen={true}
          onClose={() => setRepeatPrefill(null)}
          prefill={repeatPrefill}
          onCreated={(leadId, channel, workOrderId) => {
            setRepeatPrefill(null);
            if (channel === 'ORDER') {
              if (workOrderId) {
                // De keten converteert synchroon en geeft de WO direct terug.
                navigate(`/orders/${workOrderId}`);
              } else {
                // Fallback (keten-hardFail): pollen tot de heler-cron de
                // conversie rondmaakt (zie pendingVervolgLeadId-effect).
                setPendingVervolgLeadId(leadId);
              }
            } else {
              navigate(`/offertes/${leadId}`);
            }
          }}
        />
      )}

      {pendingVervolgLeadId && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-lg shadow-xl px-6 py-5 flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
            <p className="text-sm text-slate-700">Order wordt aangemaakt…</p>
          </div>
        </div>
      )}

      {repeatError && (
        <div className="fixed bottom-4 right-4 z-[80] bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2 rounded shadow">
          {repeatError}
          <button
            type="button"
            onClick={() => setRepeatError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >×</button>
        </div>
      )}

      <KlachtRegisterDialog />

      {addressDialogSide && (
        <AddressOverrideDialog
          orderId={order.id}
          side={addressDialogSide}
          accountId={order.customer.accountId || null}
          initial={
            addressDialogSide === 'pickup'
              ? order.addresses.pickup
              : order.addresses.delivery
          }
          onClose={() => setAddressDialogSide(null)}
          onSaved={(lmd) => {
            setOrder((prev) =>
              prev
                ? { ...prev, lastModifiedDate: lmd ?? prev.lastModifiedDate }
                : prev
            );
            refetchOrder();
            setActivityRefreshKey((k) => k + 1);
          }}
        />
      )}

      {showContactDialog && order.customer.accountId && (
        <EditOrderContactDialog
          orderId={order.id}
          accountId={order.customer.accountId}
          currentContactId={order.customer.contact?.id ?? null}
          expectedLastModifiedDate={order.lastModifiedDate}
          onClose={() => setShowContactDialog(false)}
          onSaved={(lmd) => {
            setOrder((prev) =>
              prev ? { ...prev, lastModifiedDate: lmd ?? prev.lastModifiedDate } : prev
            );
            refetchOrder();
            setActivityRefreshKey((k) => k + 1);
          }}
        />
      )}

      {showInvoiceNameDialog && (
        <EditInvoiceNameDialog
          orderId={order.id}
          accountName={order.customer.name}
          current={order.factuur ?? { andereNaam: false, naam: '', opmerkingen: '', email: '' }}
          expectedLastModifiedDate={order.lastModifiedDate}
          onClose={() => setShowInvoiceNameDialog(false)}
          onSaved={handleSaved}
        />
      )}

      {datumDialogSide && (
        <DatumWindowDialog
          orderId={order.id}
          side={datumDialogSide}
          initialDatum={
            datumDialogSide === 'pickup'
              ? order.pickupVanafDatum
              : order.deliveryVanafDatum
          }
          onClose={() => setDatumDialogSide(null)}
          onSaved={(lmd) => {
            setOrder((prev) =>
              prev
                ? { ...prev, lastModifiedDate: lmd ?? prev.lastModifiedDate }
                : prev
            );
            refetchOrder();
            setActivityRefreshKey((k) => k + 1);
          }}
        />
      )}

      {cancelAppointmentTarget && (
        <CancelAppointmentDialog
          orderId={order.id}
          ritId={cancelAppointmentTarget.ritId}
          fase={cancelAppointmentTarget.fase}
          datum={cancelAppointmentTarget.datum}
          recoveryMode={cancelAppointmentTarget.recoveryMode}
          onClose={() => setCancelAppointmentTarget(null)}
          onSaved={() => {
            setCancelAppointmentTarget(null);
            refetchOrder();
            setActivityRefreshKey((k) => k + 1);
          }}
        />
      )}

      {showMarkPickedUp && (
        <MarkPickedUpDialog
          orderId={order.id}
          orderNumber={order.orderNumber}
          onClose={() => setShowMarkPickedUp(false)}
          onSaved={() => {
            setShowMarkPickedUp(false);
            refetchOrder();
            setActivityRefreshKey((k) => k + 1);
          }}
        />
      )}

      {showNalevering && (
        <NaleveringDialog
          orderId={order.id}
          orderNumber={order.orderNumber}
          richting={showNalevering}
          items={showNalevering === 'afleveren' ? naleveringItems : naOphaalItems}
          onClose={() => setShowNalevering(null)}
          onSaved={() => {
            setShowNalevering(null);
            refetchOrder();
            setActivityRefreshKey((k) => k + 1);
          }}
        />
      )}

      {showSnoozeDialog && order.reminderSnooze && (
        <SnoozeRemindersDialog
          orderId={order.id}
          orderNumber={order.orderNumber}
          afleverRelevant={
            order.reminderSnooze.inAfleverSequence || !!order.reminderSnooze.afleverPauzeTot
          }
          ophaalRelevant={
            order.reminderSnooze.inOphaalSequence || !!order.reminderSnooze.ophaalPauzeTot
          }
          afleverPauzeTot={order.reminderSnooze.afleverPauzeTot}
          ophaalPauzeTot={order.reminderSnooze.ophaalPauzeTot}
          onClose={() => setShowSnoozeDialog(false)}
          onSaved={() => {
            setShowSnoozeDialog(false);
            refetchOrder();
            setActivityRefreshKey((k) => k + 1);
          }}
        />
      )}

      <EditWasserijItemDialog
        isOpen={!!editingItem}
        orderId={order.id}
        item={editingItem}
        huidigPakketSku={
          editingItem
            ? order.items
                .find((g) => g.primary.id === editingItem.id)
                ?.extras.find((e) => isPackageSku(e.productCode))?.productCode ?? null
            : null
        }
        onClose={() => setEditingItem(null)}
        onSaved={(lmd) => {
          setEditingItem(null);
          handleSaved(lmd);
        }}
      />

      <WasserijItemBehandelingDialog
        isOpen={!!behandelingTarget}
        parentItemId={behandelingTarget?.itemId ?? null}
        parentName={behandelingTarget?.name}
        onClose={() => {
          setBehandelingTarget(null);
          // Refetch ook bij sluiten: een behandeling kan zijn toegevoegd terwijl
          // de korting-stap faalde (dialog bleef open met melding).
          refetchOrder();
          setActivityRefreshKey((k) => k + 1);
        }}
        onAdded={() => {
          setBehandelingTarget(null);
          refetchOrder();
          setActivityRefreshKey((k) => k + 1);
        }}
      />

      <AddFeeDialog
        isOpen={showFeeDialog}
        orderId={order.id}
        onClose={() => setShowFeeDialog(false)}
        onAdded={() => {
          setShowFeeDialog(false);
          refetchOrder();
          setActivityRefreshKey((k) => k + 1);
        }}
      />

      <AddKleedDialog
        isOpen={showAddKleedDialog}
        orderId={order.id}
        onClose={() => setShowAddKleedDialog(false)}
        onAdded={(lmd) => {
          setShowAddKleedDialog(false);
          handleSaved(lmd);
        }}
      />
    </div>
  );
}
